<template>
  <LmDialog v-model="modalShow" :title="toolbarTitle">
    <template #toolbar-actions>
      <q-btn
        v-for="(action, index) of formData.actions"
        :key="`action_${index}`"
        :loading="isRequestInProgress"
        :label="action.label"
        :color="getColor(action)"
        :class="{ 'q-mr-md': index !== formData.actions.length - 1 }"
        size="sm"
        @click="performAction(action)"
      />
    </template>

    <template #content>
      <q-card-section class="dialog-content">
        <form>
          <div class="row q-col-gutter-md q-mb-md">
            <div class="col-xs-6">
              <q-input
                id="name"
                v-model="$v.formData.name.$model"
                :error="$v.formData.name.$error"
                :error-message="$v_displayMessage($v.formData.name)"
                :disable="disable('name') || !!formData.license_id"
                name="name"
                type="text"
                label="Name *"
                outlined
              />
            </div>

            <div class="col-xs-6">
              <div class="col-auto">
                <enhanced-q-select
                  v-if="publisherOptions"
                  id="provider"
                  v-model="$v.formData.publisher_id.$model"
                  :error="$v.formData.publisher_id.$error"
                  :error-message="$v_displayMessage($v.formData.publisher_id)"
                  :disable="disable('publisher_id')"
                  :class="{ disabled: disable('publisher_id') }"
                  :options="publisherOptions"
                  :label="getTranslation('licensePeriod.fields.publisher') || 'Publisher *'"
                  hide-bottom-space
                  @input="updateSubscriptions(formData.publisher_id)"
                />
                <div class="col-auto q-pt-sm">
                  <LinkButton
                    v-if="context === 'license-index'"
                    text="Create new publisher"
                    @click="publisherDialog = true"
                  />
                </div>
              </div>
            </div>
            <div class="col-xs-6">
              <enhanced-q-select
                v-if="ownerOptions"
                id="owner"
                v-model="$v.formData.owner_id.$model"
                :error="$v.formData.owner_id.$error"
                :error-message="$v_displayMessage($v.formData.owner_id)"
                :disable="disable('owner_id')"
                :class="{ disabled: disable('owner_id') }"
                :options="ownerOptions"
                :label="getTranslation('licensePeriod.fields.owner') || 'Managed by *'"
              />
            </div>
            <div class="col-xs-6">
              <LmDatePicker
                v-model="$v.formData.start_date.$model"
                :error="$v.formData.start_date.$error"
                :error-message="$v_displayMessage($v.formData.start_date)"
                :disable="disable('start_date')"
                label="Start Date *"
              />
            </div>

            <div class="col-xs-6">
              <LmDatePicker
                v-model="$v.formData.end_date.$model"
                :error="$v.formData.end_date.$error"
                :error-message="$v_displayMessage($v.formData.end_date)"
                :disable="disable('end_date')"
                :options="isDateAfterStartDate"
                clearable
                label="End Date"
              />
            </div>
            <div class="col-xs-6">
              <FileUploader v-model="formData.license_file" stack-label="License file" />
            </div>
          </div>

          <q-list separator class="no-padding q-mb-md">
            <q-expansion-item :label="getTranslation('licenses.summary') || 'Summary'" icon="fas fa-file-alt" opened>
              <q-editor
                ref="editor"
                v-model="formData.summary"
                :content-style="{ listStylePosition: 'inside' }"
                :toolbar="[
                  ['removeFormat', 'bold', 'italic', 'underline', 'hr', 'link'],
                  ['unordered', 'ordered'],
                  ['undo', 'redo']
                ]"
                :disable="disable('summary')"
                class="q-mt-lg"
                toolbar-rounded
                max-height="100px"
                toolbar-toggle-color="primary-1"
                @paste.native="Q_EDITOR_MIXIN_onPaste"
              />
            </q-expansion-item>
          </q-list>

          <q-list separator class="no-padding q-mb-md">
            <q-expansion-item
              :disable="disable('terms')"
              :label="getTranslation('licenses.terms') || 'Terms'"
              icon="fas fa-list"
            >
              <q-card>
                <q-card-section>
                  <div class="col-xs-12">
                    <lm-dt-license-terms v-if="formData.terms" v-model="formData.terms" />
                  </div>
                </q-card-section>
              </q-card>
            </q-expansion-item>
          </q-list>

          <q-list separator class="no-padding q-mb-md">
            <q-expansion-item
              :disable="!formData.publisher_id || !licenseSubscriptions || licenseSubscriptions.length === 0"
              icon="fas fa-list"
            >
              <template v-slot:header>
                <q-item-section avatar>
                  <q-icon name="fas fa-list" />
                </q-item-section>

                <q-item-section>
                  <q-item-label>
                    {{ getTranslation('entities.subscriptions') || 'Subscriptions' }}
                    <small class="q-ml-sm">
                      {{ licenseSubscriptions ? `(${licenseSubscriptions.length})` : '' }}
                    </small>
                  </q-item-label>
                </q-item-section>
              </template>

              <q-card>
                <q-card-section>
                  <div v-if="formData && licenseSubscriptions">
                    <SubscriptionExpansion
                      :selected="selectedSubscriptions"
                      :un-selected="unassignedSubscriptions"
                      @remove-subscriptions="removeSubscriptions"
                      @select-subscriptions="selectAllSubscriptions"
                    />
                  </div>
                </q-card-section>
              </q-card>
            </q-expansion-item>
          </q-list>
        </form>
      </q-card-section>
      <form-create-provider
        v-if="context === 'license-index'"
        :showing.sync="publisherDialog"
        stay-label="Return to License Form"
        go-label="Go to Publisher Page"
        namespace="publishers"
        entity-name="publisher"
        @create="newPublisherCreated"
        @close="publisherDialog = false"
      />
    </template>
  </LmDialog>
</template>

<script>
import { stripHtmlFromInput } from '@lib/formatting.utils'
import { LmFormObject } from '@/lib/models/lm-form-object.class'
import { validations } from './validations'
import { isDateValid } from '@/lib/validation-helpers'
import * as apiTo from '@/modular-api'
// Libraries
import { cloneDeep, findIndex, orderBy } from 'lodash'
import { mapState } from 'vuex'
import moment from 'moment'
// Mixins
import Q_EDITOR_MIXIN from '@/mixins/q-editor-mixin'
// Components
import LmDtLicenseTerms from '@components/lm-blocks/LicenseTerms'
import EnhancedQSelect from '@components/general/EnhancedQSelect'
import LmDatePicker from '@/reusable/LmDatePicker/LmDatePicker'
import LmDialog from '@/reusable/base-components/LmDialog/LmDialog'
import FileUploader from '@/reusable/FileUploader/FileUploader'
import LinkButton from '@/reusable/base-components/LinkButton'
import FormCreateProvider from '@components/forms/form-create-provider/FormCreateProvider'
import SubscriptionExpansion from './subscription-expansion/SubscriptionExpansion'

export default {
  name: 'FormLicensePeriod',
  components: {
    LmDtLicenseTerms,
    EnhancedQSelect,
    LmDatePicker,
    LmDialog,
    FileUploader,
    LinkButton,
    FormCreateProvider,
    SubscriptionExpansion
  },

  mixins: [validations, Q_EDITOR_MIXIN],

  props: {
    licensePeriod: {
      type: [Object, null],
      default: () => {}
    },
    editMode: {
      type: Boolean,
      default: false
    },
    renewMode: {
      type: Boolean,
      default: false
    },
    showing: {
      type: Boolean,
      default: false
    },
    disableProperties: {
      type: Array,
      default: () => []
    },
    context: String
  },

  data() {
    return {
      licenseSubscriptions: [],
      formData: {},
      style: {
        maxWidth: '100%',
        minHeight: '600px'
      },
      created: false,
      selectedCollapsed: true,
      unassignedCollapsed: true,
      isRequestInProgress: false,
      publisherDialog: false,
      publishers: [],
      createData: null
    }
  },

  computed: {
    savedPublishers() {
      return this.createData?.publishers
    },

    termLabels() {
      return this.createData?.terms
    },

    termStatuses() {
      return this.createData?.term_statuses
    },

    owners() {
      return this.createData?.owners
    },

    actions() {
      return this.createData?.actions
    },

    toolbarTitle() {
      return this.editMode ? 'Edit License Period' : 'Create License Period'
    },

    modalShow: {
      get() {
        return this.showing
      },
      set(val) {
        if (!val) {
          this.$emit('close')
          this.$v.$reset()
        }
      }
    },
    ownerOptions() {
      return this.generateOptions({ collection: this.owners })
    },
    publisherOptions() {
      return this.generateOptions({ collection: this.publishers })
    },
    selectedSubscriptions() {
      return this.licenseSubscriptions.filter(subscription => subscription.selected)
    },
    unassignedSubscriptions() {
      return this.licenseSubscriptions.filter(subscription => !subscription.selected)
    },
    isEditModeAndHasLicense() {
      return this.licensePeriod && this.editMode
    },
    isRenewModeAndHasLicense() {
      return this.licensePeriod && this.renewMode
    }
  },

  watch: {
    'formData.start_date'(val) {
      if (this.editMode || !val) return
      if (!isDateValid.call(this, val)) return
      this.formData.end_date = moment(val, this.settings.date_format)
        .add(1, 'year')
        .subtract(1, 'day')
        .format(this.settings.date_format)
    },
    modalShow(val, oldVal) {
      if (val) this.formData = this.extractLicenseInformation(this.licensePeriod)
      if (val && val !== oldVal && this.formData.publisher_id) this.updateSubscriptions(this.formData.publisher_id)
    },
    savedPublishers: {
      handler(val) {
        this.publishers = cloneDeep(val)
        this.$emit('publishers-updated', this.publishers)
      },
      immediate: true
    }
  },

  created() {
    apiTo.GET_LICENSE_PERIODS_CREATE().then(({ data }) => (this.createData = data.result))
  },

  methods: {
    newPublisherCreated(publisher) {
      this.publishers.push(publisher)
      this.formData.publisher_id = publisher.id
    },

    removeSubscriptions() {
      this.preselectSubscriptions([], this.licenseSubscriptions)
      this.formData.subscriptions = []
    },

    selectAllSubscriptions() {
      this.licenseSubscriptions.forEach(subscription => (subscription.selected = true))
    },

    extractLicenseInformation(licensePeriod) {
      const { startDate, endDate, licenseFile } = this.getPeriodInfo(licensePeriod)

      return new LmFormObject({
        id: this.isEditModeAndHasLicense ? licensePeriod.id : null,
        license_id: licensePeriod ? licensePeriod.license_id : null,
        publisher_id: licensePeriod ? licensePeriod.publisher_id : null,
        name: licensePeriod ? licensePeriod.name : null,
        start_date: startDate,
        end_date: endDate,
        summary: licensePeriod ? licensePeriod.summary : null,
        subscriptions: licensePeriod && licensePeriod.subscriptions ? licensePeriod.subscriptions : [],
        owner_id: licensePeriod ? licensePeriod.owner_id : null,
        license_file: licenseFile,
        terms: licensePeriod && (this.editMode || this.renewMode) ? licensePeriod.terms : this.termLabels,
        status: licensePeriod ? licensePeriod.status : 'pending',
        actions: licensePeriod && licensePeriod.actions ? licensePeriod.actions : this.actions
      })
    },

    getPeriodInfo(period) {
      const { BEdateFormat, formatDate, getDateAfterOneYear, userDateFormat } = this.globalVarsAndFns

      if (this.isEditModeAndHasLicense)
        return {
          startDate: formatDate(period.start_date),
          endDate: formatDate(period.end_date),
          licenseFile: period.license_file
        }

      if (this.isRenewModeAndHasLicense) {
        const startDate = moment(period.end_date, BEdateFormat)
          .add(1, 'day')
          .format(userDateFormat)
        return {
          startDate,
          endDate: getDateAfterOneYear(startDate),
          licenseFile: null
        }
      }

      const startDate = moment().format(userDateFormat)
      return {
        startDate,
        endDate: getDateAfterOneYear(startDate),
        licenseFile: null
      }
    },

    updateSubscriptions(publisherId) {
      if (!publisherId) return
      let params = {
        id: publisherId
      }
      this.$store
        .dispatch('publishers/getPublisherSubscriptions', params)
        .then(result => this.preselectSubscriptions(this.formData.subscriptions, result))
    },

    preselectSubscriptions(selected, all) {
      let result = cloneDeep(all)
      if (selected) {
        for (let subscription of result) {
          const selectedIndex = findIndex(selected, item => subscription.id === item.id)
          subscription['selected'] = selectedIndex > -1
          result = orderBy(result, ['selected', 'name'], ['asc'])
        }
      }
      this.licenseSubscriptions = result
    },

    formatData() {
      let data = cloneDeep(this.formData)
      delete data.errors
      delete data.originalData
      if (this.editMode && !(data.license_file instanceof File || data.license_file === null)) delete data.license_file
      data.start_date = this.formatDateForPost(this.formData.start_date, this.settings.date_format)
      data.end_date = this.formatDateForPost(this.formData.end_date, this.settings.date_format)

      data.terms = data.terms.map(term => {
        term.qualifiers = term.qualifiers.map(item => {
          if (typeof item === 'object') {
            if (item.hasOwnProperty('label')) return { name: item.label, id: item.value }
            return item
          }
          return term.qualifierOptions.find(o => o.id === item)
        })
        delete term.qualifierOptionsUi
        return term
      })
      data.subscriptions = this.selectedSubscriptions
      data.summary = stripHtmlFromInput(data.summary)
      let formData = new FormData()
      this.formData.getFormData(formData, data)
      if (this.editMode) formData.append('_method', 'PUT')
      return formData
    },

    process() {
      this.$v.$touch()
      if (this.$v.$invalid) return
      let params = {
        value: this.formatData()
      }
      let action = this.editMode ? 'update' : 'create'
      if (this.editMode) params.id = this.licensePeriod.id
      this.isRequestInProgress = true
      this.$store
        .dispatch(`licensePeriods/${action}`, params)
        .then(result => {
          if (this.context === 'license-index') {
            this.$router.push(`/licenses/${result.license.id}`)
            return
          }
          this.$emit('refresh', result)
          this.closeModal()
        })
        .catch(() => (this.created = false))
        .finally(() => (this.isRequestInProgress = false))
    },

    closeModal() {
      this.modalShow = false
      this.created = false
      this.licenseSubscriptions = []
    },

    processFile(event) {
      event ? this.$set(this.formData, 'license_file', event[0]) : this.$set(this.formData, 'license_file', null)
    },

    removeFile() {
      this.$set(this.formData, 'license_file', null)
    },

    disable(key) {
      return this.disableProperties.includes(key)
    },

    performAction(action) {
      this.formData.status = action.status
      return this.process()
    },

    getColor(action) {
      let color
      if (!action || action.status === 'pending') color = 'tertiary'
      else if (action.status === 'finalized') color = 'positive'
      else if (action.status === 'dismissed') color = 'blue-grey-13'
      return color
    },

    isFile(file) {
      return file instanceof File
    },

    download(id) {
      this.$store.dispatch('downloadMedia', id, { root: true })
    },

    isDateAfterStartDate(date) {
      if (!this.formData.start_date || !date) return true
      return moment(date, 'YYYY-MM-DD').isAfter(
        moment(this.formData.start_date, this.settings.date_format).format('YYYY-MM-DD')
      )
    },

    renderPeriod({ start_date, end_date }) {
      const getLocalDate = date =>
        moment
          .tz(date, this.settings.timezone)
          .local()
          .format(this.settings.date_format)
      const localStartDate = getLocalDate(start_date)
      const localEndDate = end_date ? getLocalDate(end_date) : 'One time purchase'

      return `${localStartDate} - ${localEndDate}`
    }
  }
}
</script>

<style lang="stylus" scoped>
.dialog-card {
  width: 1200px;
  max-width: 1200px;
}
.dialog-content {
  overflow auto
}
.q-table thead, .q-table th, .q-table td {
  padding 0
}
</style>
